<template>
  <window
    ref="window"
    size="md"
    :title="job ? __(`jobs.windows.request_respond.${job.typeName}.title`) : ''"
    @show="onShow"
  >
    <div class="container-fluid mt-md" slot-scope="window">
      <div v-if="singleMessage" class="row job-request-respond__message">
        <div class="col">
          <img :src="icon('info.svg')" width="15" class="mr-xxs" />
          <span class="label">{{
            __("jobs.windows.request_respond.common.labels.message")
          }}</span>
          {{ singleMessage }}

          <popover
            v-if="job.notifications && job.notifications.requests"
            target="img"
            relative-to="$parent"
            position="top-start"
            :toggle-on="['mouseenter', 'mouseleave', 'click']"
            :show-on-mounted="false"
            :show-on-updated="false"
          >
            {{ __("jobs.windows.request_respond.common.labels.message_from") }}
            {{ job.notifications.requests[0].trigger.name }}
          </popover>
        </div>
      </div>

      <div
        class="job-request-respond row mt-sm bg-none"
        v-for="(request, index) in requests"
        :key="request.date.id"
      >
        <div
          class="col-12 p-0"
          v-if="request.date.technicianRequest.status == 1"
        >
          <div class="col-6" v-if="requestValidUntilCheck(request.date.technicianRequest.validUntil)">
            {{ __("jobs.windows.request_respond.common.valid_until_prefix") }}
            {{ requestValidUntilForJob(request) }}
          </div>
          <div class="col-6" v-else>
              <p>
                  <strong>Annahmefrist abgelaufen</strong>
              </p>
          </div>
        </div>
        <job-list-date-item
          class="col col-6 d-flex pr-0"
          :date="request.date"
        ></job-list-date-item>

        <div class="col col-6 d-flex">
          <button
            type="button"
            :class="{
              'btn btn-icon btn-auto-width default-hover flex-fill mr-xxs': true,
              'btn-next': request.status == respondStatus.accepted && requestValidUntilCheck(request.date.technicianRequest.validUntil),
              'btn-gray': request.status != respondStatus.accepted || !requestValidUntilCheck(request.date.technicianRequest.validUntil),
            }"
            :disabled="request.locked || !requestValidUntilCheck(request.date.technicianRequest.validUntil)"
            @click="setResponse(request, request.locked || !requestValidUntilCheck(request.date.technicianRequest.validUntil) ? respondStatus.rejected : respondStatus.accepted)"
          >
            <img :src="icon('check-circle-white.svg')" />
          </button>
          <button
            type="button"
            :class="{
              'btn btn-icon btn-auto-width default-hover flex-fill mr-xxs': true,
              'btn-deny': request.status == respondStatus.rejected || !requestValidUntilCheck(request.date.technicianRequest.validUntil),
              'btn-gray': request.status != respondStatus.rejected && requestValidUntilCheck(request.date.technicianRequest.validUntil),
            }"
            :disabled="request.locked || !requestValidUntilCheck(request.date.technicianRequest.validUntil)"
            @click="setResponse(request, respondStatus.rejected)"
          >
            <img :src="icon('cross-circle-white.svg')" />
          </button>
        </div>

        <div
          class="job-request-respond__message col col-12 pt-xs"
          v-if="request.message && !singleMessage"
        >
          <img :src="icon('info.svg')" width="18" class="mr-xxs" />
          {{ request.message }}

          <popover
            v-if="job.notifications && job.notifications.requests[index]"
            target="img"
            relative-to="$parent"
            position="bottom-start"
            :toggle-on="['mouseenter', 'mouseleave', 'click']"
            :show-on-mounted="false"
            :show-on-updated="false"
          >
            {{ __("jobs.windows.request_respond.common.labels.message_from") }}
            {{ job.notifications.requests[index].trigger.name }}
          </popover>
        </div>

        <div
          class="job-request-respond__reason col col-12"
          v-if="request.status == respondStatus.rejected && !request.locked"
        >
          <span class="label text-center">{{
            __("jobs.windows.request_respond.common.labels.reason")
          }}</span>

          <ul class="job-request-respond__reason__items list-reset list-h">
            <li
              v-if="
                request.conflictingRequests.some(
                  (r) => r.status == respondStatus.accepted
                )
              "
            >
              <button
                :class="{
                  'btn btn-small btn-auto-width': true,
                  'btn-secondary': request.reason !== 0,
                  'btn-primary': request.reason === 0,
                }"
                @click="setReason(request, 0)"
              >
                {{
                  __(
                    "jobs.windows.request_respond.common.reasons.date_conflict"
                  )
                }}
              </button>
            </li>
            <li
              v-for="reason in app.options.Reason.REASONS.rejected"
              :key="reason.id"
              class="ml-xs mr-xs"
            >
              <button
                :class="{
                  'btn btn-small btn-auto-width': true,
                  'btn-secondary': request.reason != reason.id,
                  'btn-primary': request.reason == reason.id,
                }"
                @click="setReason(request, reason.id)"
              >
                {{ reason.reason }}
              </button>
            </li>
          </ul>
        </div>
      </div>

      <div class="row">
        <div class="buttons col">
          <button
            type="button"
            class="btn btn-secondary"
            @click="window.hide()"
          >
            {{ __("main.buttons.cancel") }}
          </button>
          <button
            type="button"
            class="btn btn-primary"
            :disabled="!readyForSave"
            @click="save"
          >
            {{ __("main.buttons.confirm") }}
          </button>
        </div>
      </div>
    </div>
  </window>
</template>

<script>
import {
  requestValidUntilOutput,
  requestValidUntilOutputPerJob,
} from "../helper/jobs";
import ShowsJobDateItems from "../mixins/ShowsJobDateItems";

export default {
  mixins: [ShowsJobDateItems],
  provide() {
    return {
      windowId: "jobRequestRespond",
    };
  },
  props: {
    job: {
      type: Object,
      required: true,
    },
    status: {
      type: Number,
    },
    date: {
      type: Object,
    },
  },
  data() {
    return {
      requests: [],
    };
  },
  computed: {
    respondStatus() {
      return this.app.options.JobTechnicianRequest.STATUS;
    },
    validUntilOutput() {
      return this.job ? requestValidUntilOutput(this.job) : "";
    },
    readyForSave() {
      return this.requests.every(
        (r) => r.status && (r.status != 3 || r.reason !== null)
      );
    },
    singleMessage() {
      const firstDateWithRequest = this.job.dates.find(
        (date) => date.technicianRequest
      );
      const firstMessage = firstDateWithRequest
        ? firstDateWithRequest.technicianRequest.message
        : null;

      return firstMessage &&
        this.job.dates.every(
          (date) =>
            date.technicianRequest &&
            date.technicianRequest.message == firstMessage
        )
        ? firstMessage
        : null;
    },
  },
  methods: {
    requestValidUntilForJob(request_date) {
      return requestValidUntilOutputPerJob(request_date.date.technicianRequest);
    },

    requestValidUntilCheck(request_date) {
      let isValid = true;
      if(request_date <= new Date()) {
        //Do something..
        isValid = false;
      }
      return isValid;
    },

    onShow() {
      this.requests = this.createRequests(this.job, this.status, this.date);
    },
    createRequests(job, defaultStatus, date = null) {
      const requests = [];

      for (const date of job.dates) {
        if (date.technicianRequest) {
          requests.push(
            Vue.observable({
              date: date,
              dateStart: new Date(date.start),
              dateEnd: new Date(date.end),
              message: date.technicianRequest.message,
              status:
                date.technicianRequest.status ==
                this.app.options.JobTechnicianRequest.STATUS.requested
                  ? defaultStatus
                  : date.technicianRequest.status,
              locked:
                date.technicianRequest.status !=
                this.app.options.JobTechnicianRequest.STATUS.requested,
              reason: date.technicianRequest.reason_id,
              conflictingRequests: [],
            })
          );
        }
      }

      const displayRequests = [];

      for (const request of requests) {
        request.conflictingRequests = requests.filter((r) => {
          return (
            (r.date.id != request.date.id &&
            !(r.dateEnd <= request.dateStart || r.dateStart >= request.dateEnd)) || r.date.technicianRequest.validUntil < new Date()
          );
        });

        if (defaultStatus !== null && (!date || date.id == request.date.id)) {
          this.setResponse(request, request.status);
        }

        if (
          !date ||
          date.id == request.date.id ||
          request.conflictingRequests.length
        ) {
          displayRequests.push(request);
        }
      }

      return displayRequests;
    },
    setResponse(request, status) {
      request.status = status;

      if (
        status == this.respondStatus.accepted &&
        request.conflictingRequests.length
      ) {
        for (const conflictingRequest of request.conflictingRequests) {
          conflictingRequest.status = this.respondStatus.rejected;
          conflictingRequest.reason = 0;
        }
      }
    },
    setReason(request, reasonId) {
      request.reason = reasonId;
    },
    save() {
      this.$store.dispatch("jobs/respondRequests", {
        job: this.job,
        responses: this.requests.reduce((responses, request) => {
          if (!request.locked) {
            responses[request.date.id] = {
              status: request.status,
              reason:
                request.status == this.respondStatus.rejected
                  ? request.reason
                  : null,
            };
          }

          return responses;
        }, {}),
      });

      this.$refs.window.hide();
    },
  },
};
</script>

<style lang="scss">
.job-request-respond {
  padding-top: map-get($spacers, sm);
  padding-bottom: map-get($spacers, sm);

  &:nth-child(even) {
    background-color: $bg-light;
  }

  > .col {
    &:first-child {
      padding-left: map-get($spacers, sm);
    }
    &:last-child {
      padding-right: map-get($spacers, sm);
    }
  }
  .btn-next[disabled] {
    background: #47b275;
    border-color: #47b275;
  }
  .btn-gray[disabled] {
    background: #c0c0c0;
    border-color: #c0c0c0;
  }
  .btn-deny[disabled] {
    background: #e02020;
    border-color: #e02020;
  }
  .job-list-item {
    &__date {
      .job-date {
        padding-left: map-get($spacers, mdsm);
        padding-right: map-get($spacers, mdsm);
        font-size: 0.8em;

        &--duration {
          display: flex;
          align-items: center;
        }
      }
    }
  }

  &__message {
    .label {
      display: inline;
      margin-bottom: 0 !important;
    }
  }

  &__reason {
    padding: map-get($spacers, xs) 0 map-get($spacers, sm) 0;
    margin-top: map-get($spacers, sm);

    &__items {
      justify-content: center;

      > li {
        margin-bottom: map-get($spacers, sm);
      }
    }
  }

  &:nth-child(even) &__reason {
    background-color: rgba($white, 0.7);
  }
  &:nth-child(odd) &__reason {
    background-color: rgba($bg-light, 0.7);
  }
}
.bg-none {
  background-color: transparent !important;
}
</style>